import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { profileSchema, validateData, getValidationErrors } from "@/utils/validation";
import { z } from "zod";
import { logger } from "@/utils/logger";

export type UserRole = 'ADMIN' | 'SUPERVISOR' | 'PADRAO' | 'VISUALIZACAO';

export interface Profile {
  id: string;
  email: string | null;
  full_name: string | null;
  avatar_url: string | null;
  role: UserRole;
  matricula: string | null;
  telefone: string | null;
  nome_guerra: string | null;
  unidade_lotacao: string | null;
  usuario_sistema: boolean;
  pode_ser_membro_equipe: boolean;
  nome_usuario: string | null;
  habilidades_competencias: string | null;
  observacoes: string | null;
  can_create_projects?: boolean;
  can_create_contracts?: boolean;
  can_create_action_plans?: boolean;
  can_create_strategic_items?: boolean;
  can_view_dashboard?: boolean;
  can_view_projects?: boolean;
  can_view_contracts?: boolean;
  can_view_portfolios?: boolean;
  can_view_strategic?: boolean;
  can_view_calendar?: boolean;
  can_view_tasks?: boolean;
  can_view_reports_overview_projects?: boolean;
  can_view_reports_overview_contracts?: boolean;
  can_view_reports_financial?: boolean;
  can_view_reports_users?: boolean;
  can_view_reports_areas?: boolean;
  deleted_at?: string | null;
  last_login_at?: string | null;
  created_at: string;
  updated_at: string;
}

export const useProfiles = () => {
  const { toast } = useToast();

  return useQuery({
    queryKey: ['profiles'],
    queryFn: async () => {
      logger.log('[useProfiles] Iniciando busca de perfis...');
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .is('deleted_at', null)
        .order('full_name');

      if (error) {
        logger.error('[useProfiles] Erro ao carregar perfis:', error.code);
        
        toast({
          title: "Erro ao carregar perfis",
          description: `${error.message}${error.hint ? ` (Dica: ${error.hint})` : ''}`,
          variant: "destructive",
        });
        throw error;
      }

      logger.log(`[useProfiles] ${data?.length || 0} perfis carregados`);
      return data as Profile[];
    },
    staleTime: 10 * 60 * 1000, // Cache por 10 minutos - profiles mudam raramente
    gcTime: 30 * 60 * 1000, // Manter em cache por 30 minutos
    retry: 2, // Tentar novamente até 2 vezes em caso de falha
    retryDelay: 1000, // Aguardar 1 segundo entre tentativas
  });
};

export const useUpdateUserRole = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: UserRole }) => {
      const { data, error } = await supabase
        .from('profiles')
        .update({ role })
        .eq('id', userId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profiles'] });
      toast({
        title: "Sucesso",
        description: "Papel do usuário atualizado com sucesso!",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Erro ao atualizar papel",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

// Interface estendida para atualização que inclui campos que vão para o Auth
interface ProfileUpdateData extends Partial<Profile> {
  email_login?: string;  // Campo especial que atualiza auth.users.email + profiles.nome_usuario
  can_create_projects?: boolean;
  can_create_contracts?: boolean;
  can_create_action_plans?: boolean;
  can_create_strategic_items?: boolean;
}

export const useUpdateProfile = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ userId, data, syncAuth = false }: { userId: string; data: ProfileUpdateData; syncAuth?: boolean }) => {
      // Extrair email_login antes de validar (não faz parte do profileSchema)
      const { email_login, ...profileData } = data;
      
      // Validar dados de perfil antes de atualizar
      const validatedData = profileSchema.partial().parse(profileData);
      
      // Se syncAuth for true, sincronizar com Supabase Auth via edge function
      if (syncAuth) {
        const { data: authResult, error: authError } = await supabase.functions.invoke('update-user', {
          body: {
            target_user_id: userId,
            email: validatedData.email,
            email_login: email_login,
            full_name: validatedData.full_name,
            matricula: validatedData.matricula,
            role: validatedData.role,
            can_create_projects: data.can_create_projects,
            can_create_contracts: data.can_create_contracts,
            can_create_action_plans: data.can_create_action_plans,
            can_create_strategic_items: data.can_create_strategic_items,
          }
        });

        if (authError) {
          logger.error('[useUpdateProfile] Erro ao sincronizar com Auth:', authError);
          // Tentar extrair a mensagem real do corpo da resposta
          let realMessage = authError.message;
          let statusCode: number | undefined;
          try {
            const resp = (authError as any)?.context?.response;
            if (resp && typeof resp.json === 'function') {
              statusCode = resp.status;
              const body = await resp.clone().json();
              if (body?.error) realMessage = body.error;
            }
          } catch {
            // ignore
          }
          if (statusCode === 401) {
            throw new Error('Sua sessão expirou. Faça logout e login novamente para continuar.');
          }
          throw new Error(realMessage || 'Erro ao sincronizar com autenticação');
        }
        
        if (authResult && 'error' in authResult) {
          throw new Error(authResult.error as string);
        }
        
        logger.log('[useUpdateProfile] Sincronizado com Auth:', authResult);
      }
      
      // Atualizar campos no profiles (exceto nome_usuario que é atualizado via edge function)
      const { data: result, error } = await supabase
        .from('profiles')
        .update(validatedData)
        .eq('id', userId)
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profiles'] });
      toast({
        title: "Sucesso",
        description: "Perfil atualizado com sucesso!",
      });
    },
    onError: (error: any) => {
      const errorMessage = error instanceof z.ZodError 
        ? getValidationErrors(error)
        : error.message;
      
      toast({
        title: "Erro ao atualizar perfil",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });
};

export const useCreateUser = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ 
      email, 
      fullName, 
      role, 
      usuarioSistema, 
      podeSerMembroEquipe,
      canCreateProjects,
      canCreateContracts,
      canCreateActionPlans,
      canCreateStrategicItems,
      canViewDashboard,
      canViewProjects,
      canViewContracts,
      canViewPortfolios,
      canViewStrategic,
      canViewCalendar,
      canViewTasks,
      canViewReportsOverviewProjects,
      canViewReportsOverviewContracts,
      canViewReportsFinancial,
      canViewReportsUsers,
      canViewReportsAreas,
      matricula,
      telefone,
      nomeGuerra,
      unidadeLotacao,
      habilidadesCompetencias,
      observacoes,
      avatarUrl,
      password
    }: { 
      email: string; 
      fullName: string; 
      role: UserRole;
      usuarioSistema: boolean;
      podeSerMembroEquipe: boolean;
      canCreateProjects?: boolean;
      canCreateContracts?: boolean;
      canCreateActionPlans?: boolean;
      canCreateStrategicItems?: boolean;
      canViewDashboard?: boolean;
      canViewProjects?: boolean;
      canViewContracts?: boolean;
      canViewPortfolios?: boolean;
      canViewStrategic?: boolean;
      canViewCalendar?: boolean;
      canViewTasks?: boolean;
      canViewReportsOverviewProjects?: boolean;
      canViewReportsOverviewContracts?: boolean;
      canViewReportsFinancial?: boolean;
      canViewReportsUsers?: boolean;
      canViewReportsAreas?: boolean;
      matricula?: string;
      telefone?: string;
      nomeGuerra?: string;
      unidadeLotacao?: string;
      habilidadesCompetencias?: string;
      observacoes?: string;
      avatarUrl?: string;
      password: string; // Client-generated password - required
    }) => {
      // Validar dados antes de enviar
      const validatedData = profileSchema.parse({
        email,
        full_name: fullName,
        role,
        matricula: matricula || null,
        telefone: telefone || null,
        nome_guerra: nomeGuerra || null,
        unidade_lotacao: unidadeLotacao || null,
        habilidades_competencias: habilidadesCompetencias || null,
        observacoes: observacoes || null,
        avatar_url: avatarUrl || null,
        usuario_sistema: usuarioSistema,
        pode_ser_membro_equipe: podeSerMembroEquipe,
      });
      
      // Send client-generated password - never returned in response for security
      const { data, error } = await supabase.functions.invoke('create-user', {
        body: {
          email: validatedData.email,
          fullName: validatedData.full_name,
          role: validatedData.role,
          usuarioSistema: validatedData.usuario_sistema,
          podeSerMembroEquipe: validatedData.pode_ser_membro_equipe,
          canCreateProjects: !!canCreateProjects,
          canCreateContracts: !!canCreateContracts,
          canCreateActionPlans: !!canCreateActionPlans,
          canCreateStrategicItems: !!canCreateStrategicItems,
          canViewDashboard: canViewDashboard ?? true,
          canViewProjects: canViewProjects ?? true,
          canViewContracts: canViewContracts ?? true,
          canViewPortfolios: canViewPortfolios ?? true,
          canViewStrategic: canViewStrategic ?? true,
          canViewCalendar: canViewCalendar ?? true,
          canViewTasks: canViewTasks ?? true,
          canViewReportsOverviewProjects: canViewReportsOverviewProjects ?? true,
          canViewReportsOverviewContracts: canViewReportsOverviewContracts ?? true,
          canViewReportsFinancial: canViewReportsFinancial ?? true,
          canViewReportsUsers: canViewReportsUsers ?? true,
          canViewReportsAreas: canViewReportsAreas ?? true,
          matricula: validatedData.matricula,
          telefone: validatedData.telefone,
          nomeGuerra: validatedData.nome_guerra,
          unidadeLotacao: validatedData.unidade_lotacao,
          habilidadesCompetencias: validatedData.habilidades_competencias,
          observacoes: validatedData.observacoes,
          avatarUrl: validatedData.avatar_url,
          password: password // Client-generated password
        }
      });

      if (error) {
        // Try to parse the response body from FunctionsHttpError context (data may be null on non-2xx)
        let body: any = data;
        try {
          const resp = (error as any)?.context?.response;
          if (resp && typeof resp.json === 'function') {
            body = await resp.clone().json();
          }
        } catch {
          // ignore parse errors, fall back to data
        }

        const duplicateCodes = ['MATRICULA_EXISTS', 'EMAIL_EXISTS', 'DUPLICATE_USERNAME', 'MATRICULA_SOFT_DELETED', 'EMAIL_SOFT_DELETED'];
        const isDuplicate = body?.code && duplicateCodes.includes(body.code);
        const friendlyMessage = isDuplicate
          ? `Usuário já cadastrado. ${body?.error || ''}`.trim()
          : (body?.error || error.message);

        const enhancedError: any = new Error(friendlyMessage);
        enhancedError.code = body?.code || '';
        enhancedError.existingUserId = body?.existingUserId || '';
        enhancedError.existingUserName = body?.existingUserName || '';
        enhancedError.duplicateInfo = body?.code ? {
          code: body.code,
          existingUserId: body.existingUserId || '',
          existingUserName: body.existingUserName || '',
          message: friendlyMessage,
        } : undefined;
        throw enhancedError;
      }
      if (!data.success) throw new Error(data.error);
      
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['profiles'] });
      toast({
        title: "Sucesso",
        description: data.message || "Usuário criado com sucesso!",
      });
    },
    onError: (error: any) => {
      const duplicateCodes = ['MATRICULA_EXISTS', 'EMAIL_EXISTS', 'DUPLICATE_USERNAME', 'MATRICULA_SOFT_DELETED', 'EMAIL_SOFT_DELETED'];
      
      // duplicateInfo is already attached in mutationFn
      if (error.duplicateInfo && duplicateCodes.includes(error.duplicateInfo.code)) {
        // Don't show toast - let the component handle it via error.duplicateInfo
        return;
      }
      
      const displayMessage = error instanceof z.ZodError 
        ? getValidationErrors(error)
        : error.message;
      
      toast({
        title: "Erro ao criar usuário",
        description: displayMessage,
        variant: "destructive",
      });
    },
  });
};

export const useResetPassword = () => {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ userId, password }: { userId: string; password: string }) => {
      // Garantir que a sessão do admin ainda é válida antes de chamar a função
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData?.session) {
        // Tenta refresh; se falhar, exige novo login
        const { data: refreshed, error: refreshErr } = await supabase.auth.refreshSession();
        if (refreshErr || !refreshed?.session) {
          throw new Error("Sua sessão expirou. Faça login novamente para resetar a senha do usuário.");
        }
      }

      // Send client-generated password - never returned in response for security
      const { data, error } = await supabase.functions.invoke('reset-password', {
        body: { 
          target_user_id: userId,
          password: password // Client-generated password
        }
      });

      if (error) throw error;
      if (data && 'error' in data) {
        throw new Error(data.error as string);
      }
      
      return data;
    },
    onSuccess: (data: any) => {
      const emailFailed = data?.email_sent === false && data?.email_skipped === false;
      toast({
        title: data?.email_sent ? "Senha resetada e enviada por e-mail" : "Senha Resetada",
        description: data?.message || "A senha foi resetada com sucesso. O usuário deve alterar no próximo login.",
        variant: emailFailed ? "destructive" : "default",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Erro ao resetar senha",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (userId: string) => {
      const { data, error } = await supabase.rpc('soft_delete_user', {
        target_user_id: userId
      });
      if (error) throw error;
      return { id: userId, success: data };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profiles'] });
      toast({
        title: "Usuário Excluído",
        description: "O usuário foi excluído com sucesso. Suas atividades anteriores foram preservadas.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Erro ao excluir usuário",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

export const useReactivateUser = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (params: {
      userId: string;
      fullName?: string;
      email?: string;
      matricula?: string;
      role?: UserRole;
      canCreateProjects?: boolean;
      canCreateContracts?: boolean;
      canCreateActionPlans?: boolean;
      canCreateStrategicItems?: boolean;
    }) => {
      const { data, error } = await (supabase.rpc as any)('reactivate_user', {
        target_user_id: params.userId,
        new_full_name: params.fullName ?? null,
        new_email: params.email ?? null,
        new_matricula: params.matricula ?? null,
        new_role: params.role ?? null,
        new_can_create_projects: params.canCreateProjects ?? null,
        new_can_create_contracts: params.canCreateContracts ?? null,
        new_can_create_action_plans: params.canCreateActionPlans ?? null,
        new_can_create_strategic_items: params.canCreateStrategicItems ?? null,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profiles'] });
      toast({
        title: "Usuário reativado",
        description: "O usuário foi reativado. Reset a senha em seguida para liberar o acesso.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Erro ao reativar usuário",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};